/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  FunnelChart,
  Funnel,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import { extractValueInsideCurlyBraces, replacePlaceholdersWithVariables, parseIfJSON } from '../../utils/base';

type ChartItemBase = {
  id: string;
  component: string;
  data?: string;
  axis?: 'x' | 'y';
  style?: any;
  items?: ChartItemBase[];
};

type ChartRendererProps = {
  chartItems: ChartItemBase[];
  settingsData?: string;
  localVariables?: any;
  width?: string | number;
  height?: string | number;
};

const getStrokeStyle = (stroke: any) => {
  if (!stroke) return {};

  const style: any = {};

  if (stroke.color) style.stroke = stroke.color;
  if (stroke.width) style.strokeWidth = stroke.width;
  if (stroke.style === 'dashed' && stroke.dash && stroke.gap) {
    style.strokeDasharray = `${stroke.dash} ${stroke.gap}`;
  }

  return style;
};

const getBackgroundColor = (background: any[]): string | undefined => {
  if (!Array.isArray(background) || background.length === 0) return undefined;

  const colorBg = background.find((bg) => bg.type === 'color');
  return colorBg?.value;
};

const ChartRenderer: React.FC<ChartRendererProps> = ({
  chartItems,
  settingsData,
  localVariables,
  width = '100%',
  height = '100%',
}) => {
  const realData = parseIfJSON(settingsData);
  const chartData = Array.isArray(realData) ? realData : [];
    // Normalize width and height for ResponsiveContainer
  const containerWidth = typeof width === 'number' ? width : width;
  const containerHeight = typeof height === 'number' ? height : height;

  const chartConfig = useMemo(() => {
    const config = {
      type: 'line' as 'line' | 'bar' | 'pie' | 'funnel',
      series: [] as any[],
      xAxis: null as any,
      yAxis: null as any,
      grid: null as any,
      tooltip: false,
      legend: false,
    };

    chartItems.forEach((item) => {
      switch (item.component) {
        case 'chart-line':
          config.type = 'line';
          config.series.push({
            type: 'line',
            id: item.id,
            dataKey: extractValueInsideCurlyBraces(item.data || ''),
            ...getStrokeStyle(item.style?.stroke),
            strokeType: item.style?.type || 'monotone',
            animationDuration: item.style?.animation?.duration || 0,
            animationEasing: item.style?.animation?.easing,
            dot: item.items?.find((subItem) => subItem.component === 'chart-dot'),
          });
          break;

        case 'chart-bar':
          config.type = 'bar';
          config.series.push({
            type: 'bar',
            id: item.id,
            dataKey: extractValueInsideCurlyBraces(item.data || ''),
            fill: getBackgroundColor(item.style?.background) || '#8884d8',
            animationDuration: item.style?.animation?.duration || 0,
          });
          break;

        case 'chart-pie':
          config.type = 'pie';
          config.series.push({
            type: 'pie',
            id: item.id,
            dataKey: extractValueInsideCurlyBraces(item.data || ''),
            nameKey: item.style?.nameKey,
            fill: getBackgroundColor(item.style?.background) || '#8884d8',
            innerRadius: item.style?.innerRadius,
            outerRadius: item.style?.outerRadius,
            animationDuration: item.style?.animation?.duration || 0,
          });
          break;

        case 'chart-funnel':
          config.type = 'funnel';
          config.series.push({
            type: 'funnel',
            id: item.id,
            dataKey: extractValueInsideCurlyBraces(item.data || ''),
            fill: getBackgroundColor(item.style?.background) || '#8884d8',
            animationDuration: item.style?.animation?.duration || 0,
          });
          break;

        case 'chart-axis':
          if (item.axis === 'x') {
            config.xAxis = {
              dataKey: extractValueInsideCurlyBraces(item.data || ''),
              ...getStrokeStyle(item.style?.stroke),
              domain: item.style?.domain,
              type: item.style?.type,
            };
          } else if (item.axis === 'y') {
            config.yAxis = {
              ...getStrokeStyle(item.style?.stroke),
              domain: item.style?.domain,
              type: item.style?.type,
            };
          }
          break;

        case 'chart-grid':
          config.grid = {
            ...getStrokeStyle(item.style?.stroke),
          };
          break;

        case 'chart-tooltip':
          config.tooltip = true;
          break;

        case 'chart-legend':
          config.legend = true;
          break;

        default:
          break;
      }
    });

    return config;
  }, [chartItems]);

  if (!chartData.length) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
        fontSize: '14px',
      }}>
        No data available
      </div>
    );
  }

  const renderDot = (dotConfig: any) => {
    if (!dotConfig) return undefined;

    const DotComponent = (props: any) => {
      const { cx, cy } = props;
      const dotStyle = dotConfig.style || {};
      const dotWidth = parseFloat(dotStyle.width) || 8;
      const dotColor = getBackgroundColor(dotStyle.background) || '#8884d8';
      const borderRadius = dotStyle.border?.radius || '0%';

      return (
        <circle
          cx={cx}
          cy={cy}
          r={dotWidth / 2}
          fill={dotColor}
          style={{ borderRadius }}
        />
      );
    };
    DotComponent.displayName = 'DotComponent';
    return DotComponent;
  };

  const renderChart = () => {
    switch (chartConfig.type) {
      case 'line':
        return (
          <ResponsiveContainer width={containerWidth as any} height={containerHeight as any}>
            <LineChart data={chartData}>
              {chartConfig.grid && <CartesianGrid {...chartConfig.grid} />}
              {chartConfig.xAxis && (
                <XAxis 
                  {...chartConfig.xAxis}
                  domain={chartConfig.xAxis.domain}
                  type={chartConfig.xAxis.type}
                />
              )}
              {chartConfig.yAxis && (
                <YAxis 
                  {...chartConfig.yAxis}
                  domain={chartConfig.yAxis.domain}
                  type={chartConfig.yAxis.type}
                />
              )}
              {chartConfig.tooltip && <Tooltip />}
              {chartConfig.legend && <Legend />}
              {chartConfig.series.map((series: any) => (
                <Line
                  key={series.id}
                  type={series.strokeType}
                  dataKey={series.dataKey}
                  stroke={series.stroke}
                  strokeWidth={series.strokeWidth}
                  strokeDasharray={series.strokeDasharray}
                  animationDuration={series.animationDuration}
                  animationEasing={series.animationEasing}
                  dot={renderDot(series.dot)}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width={containerWidth as any} height={containerHeight as any}>
            <BarChart data={chartData}>
              {chartConfig.grid && <CartesianGrid {...chartConfig.grid} />}
              {chartConfig.xAxis && (
                <XAxis 
                  {...chartConfig.xAxis}
                  domain={chartConfig.xAxis.domain}
                  type={chartConfig.xAxis.type}
                />
              )}
              {chartConfig.yAxis && (
                <YAxis 
                  {...chartConfig.yAxis}
                  domain={chartConfig.yAxis.domain}
                  type={chartConfig.yAxis.type}
                />
              )}
              {chartConfig.tooltip && <Tooltip />}
              {chartConfig.legend && <Legend />}
              {chartConfig.series.map((series: any) => (
                <Bar
                  key={series.id}
                  dataKey={series.dataKey}
                  fill={series.fill}
                  animationDuration={series.animationDuration}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width={containerWidth as any} height={containerHeight as any}>
            <PieChart>
              {chartConfig.tooltip && <Tooltip />}
              {chartConfig.legend && <Legend />}
              {chartConfig.series.map((series: any) => (
                <Pie
                  key={series.id}
                  data={chartData}
                  dataKey={series.dataKey}
                  nameKey={series.nameKey || 'name'}
                  cx="50%"
                  cy="50%"
                  innerRadius={series.innerRadius}
                  outerRadius={series.outerRadius || 80}
                  fill={series.fill}
                  animationDuration={series.animationDuration}
                  label
                >
                  {chartData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={series.fill} />
                  ))}
                </Pie>
              ))}
            </PieChart>
          </ResponsiveContainer>
        );

      case 'funnel':
        return (
          <ResponsiveContainer width={containerWidth as any} height={containerHeight as any}>
            <FunnelChart>
              {chartConfig.tooltip && <Tooltip />}
              {chartConfig.legend && <Legend />}
              {chartConfig.series.map((series: any) => (
                <Funnel
                  key={series.id}
                  data={chartData}
                  dataKey={series.dataKey}
                  fill={series.fill}
                  animationDuration={series.animationDuration}
                />
              ))}
            </FunnelChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return <>{renderChart()}</>;
};

export default ChartRenderer;
